import React, { useEffect, useRef, useState } from 'react';
import { Grid, GridProps, Icon, Segment } from 'semantic-ui-react';
import { useResponsive } from 'ahooks';
import _ from 'lodash';

import { SemanticWIDTHS } from 'semantic-ui-react/dist/commonjs/generic';
import { LoadingCard } from 'src/components/coreUI';
import { useLoadMore, useURLState } from 'src/hooks';

export type Responsive = {
  xs?: SemanticWIDTHS;
  sm?: SemanticWIDTHS;
  md?: SemanticWIDTHS;
  lg?: SemanticWIDTHS;
  xl?: SemanticWIDTHS;
};

type Props<T extends any> = {
  data?: T[];
  renderItem: (data: T, index: number) => React.ReactElement;
  placeholder?: React.ReactElement;
  shouldShowEmpty?: boolean;
  onIntersection?: (
    isIntersect: boolean,
    next: number,
    search: any,
  ) => Promise<void>;
} & GridProps;

function List<T extends any>(props: Props<T>) {
  const {
    data = [],
    renderItem,
    children,
    placeholder,
    onIntersection,
    shouldShowEmpty,
    tail,
    ...rest
  } = props;

  const screen = useResponsive();
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const { hasLoadMore, nextPage, isInViewPort } = useLoadMore(loadMoreRef);
  const [isLoadMore, setIsLoadMore] = useState<boolean>(false);
  const [search] = useURLState<SearchParams.Base>({});

  useEffect(() => {
    const handleLoadMore = async () => {
      if (isLoadMore) return;

      setIsLoadMore(true);
      await onIntersection?.(hasLoadMore && isInViewPort, nextPage, search);
      setIsLoadMore(false);
    };

    handleLoadMore();
  }, [hasLoadMore, isInViewPort, nextPage]);

  return (
    <Grid columns={screen.sm ? 2 : 1} stretched {...rest}>
      {data.length
        ? data.map((d, i) => {
            return (
              <Grid.Column key={i}>
                {tail && <span className="tail" />}
                {renderItem(d, i)}
              </Grid.Column>
            );
          })
        : null}

      {placeholder && isLoadMore
        ? _.times(Number(props.columns) || 2).map((i) => (
            <Grid.Column key={i}>
              <LoadingCard>{placeholder}</LoadingCard>
            </Grid.Column>
          ))
        : null}

      {data.length === 0 && !isLoadMore && shouldShowEmpty && (
        <Segment placeholder className="w-100 bg-transparent shadow-none">
          <div className="d-flex flex-col justify-center align-center">
            <Icon color="purple" size="huge" name="search" />
            <span className="fw-bold text-primary mt-12">{`No items have been found.`}</span>
          </div>
        </Segment>
      )}

      {children}

      {placeholder && hasLoadMore && <div ref={loadMoreRef} className="row" />}
    </Grid>
  );
}

export default List;
